import { Button, CheckmarkInput, Header, IconWithCustomColor, StarIcon } from "@datagame/ui";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";

import { isWordPresent } from "../../../utils/isWordPresent";
import { ProgressBar } from "../../shared/ProgressBar";
import { Timer } from "../../shared/Timer";
import { useTime } from "../../../hooks/useTime";
import { useWordopolis } from "../../../hooks/useWordopolis";
import { WordopolisActionKind } from "../../../reducers/wordopolis";
import { WordopolisDifficulty } from "../../../gql/graphql";
import WordoplisMedium from "../../../assets/images/wordopolisMedium.png";
import WordoplisMiniMap from "../../../assets/images/wordopolisMiniMap.png";
import WordopolisEasy from "../../../assets/images/wordopolisEasy.png";
import WordopolisHard from "../../../assets/images/wordopolisHard.png";
import WordopolisZenMode from "../../../assets/images/zenMode.svg";

import styles from "./WordopolisPanel.module.scss";

const difficultyImages = {
  [WordopolisDifficulty.Easy]: WordopolisEasy,
  [WordopolisDifficulty.Medium]: WordoplisMedium,
  [WordopolisDifficulty.Hard]: WordopolisHard,
};

export const WordopolisPanel = () => {
  const [
    {
      configuration: { difficulty, isTimeLimited, words, topic, gameVisuals },
      status: { foundWords, isGameFinished },
      results,
    },
    dispatch,
  ] = useWordopolis();
  const { themeColor } = gameVisuals;
  const { getCurrentTime } = useTime();
  const isDesktop = useMediaQuery({ query: "(min-width: 1280px)" });
  const { t } = useTranslation("common");
  const totalScore = Object.values(results).reduce((acc, { score }) => acc + score, 0);

  const handleFinishGame = () => {
    dispatch({ type: WordopolisActionKind.END_GAME, payload: { time: getCurrentTime() } });
  };

  const timer = isTimeLimited ? (
    <Timer />
  ) : (
    <img src={WordopolisZenMode} alt="zen-mode" className={styles.zenModeIcon} />
  );

  return (
    <div className={styles.wordopolisPanel}>
      <div className={styles.panelHeading}>
        <div className={styles.sideWrapper}>
          {!isDesktop && (
            <Button
              kind="text"
              customColor={themeColor}
              className={styles.mobileFinishButton}
              onClick={handleFinishGame}
            >
              {t("finish")}
            </Button>
          )}
          <div className={styles.imageInfo}>
            <img src={difficultyImages[difficulty]} alt="difficulty-level" />
            <span className={styles.text}>{t(`wordopolis.difficulty.${difficulty}`)}</span>
          </div>
        </div>
        <div className={styles.timerWrapper}>{timer}</div>
        <div className={styles.sideWrapper}>
          <div className={styles.scoreBadge}>
            <IconWithCustomColor icon={<StarIcon />} innerFill={themeColor} className={styles.scoreIcon} />
            <Header variant="h6" className={styles.scoreText}>
              {totalScore}
            </Header>
          </div>
          {!isDesktop && (
            <div className={styles.imageInfo}>
              <img src={WordoplisMiniMap} alt="zoom-level" />
            </div>
          )}
        </div>
      </div>
      <div className={styles.categoryWrapper}>
        <Header variant="h6" className={styles.taskDescription}>
          {t("wordopolis.taskDescription")}
        </Header>
        <Header variant="h5" className={styles.categoryHeader}>
          {topic}
        </Header>
        <Header variant="h6" className={styles.progressLabel}>
          {t("wordopolis.result.part1")} {foundWords.length}{" "}
          <span className={styles.grayLabel}>
            {t("wordopolis.result.part2")} {words.length}
          </span>
        </Header>
      </div>
      <ProgressBar
        color={themeColor}
        className={styles.gameProgressBar}
        currentStep={foundWords.length}
        steps={words.length}
      />
      {isDesktop && (
        <div className={styles.desktopPanel}>
          <div className={styles.wordList}>
            <Header className={styles.wordListHeader} variant="h6">
              {t("wordopolis.wordList")}:
            </Header>
            <div className={styles.list}>
              {words.map(({ value, description }, index) => (
                <CheckmarkInput
                  disabled={true}
                  type="checkbox"
                  key={index}
                  id={value}
                  name="word"
                  customColor={themeColor}
                  value={isWordPresent(value, foundWords)}
                  label={description ?? value.toUpperCase()}
                  unchangable={true}
                />
              ))}
            </div>
          </div>
          <Button
            onClick={handleFinishGame}
            customColor={themeColor}
            className={styles.desktopFinishButton}
            disabled={isGameFinished}
          >
            {t("finishGame")}
          </Button>
        </div>
      )}
    </div>
  );
};
